import { ReactElement, useEffect } from 'react';
import { LogoutModal } from 'components/common/Modal/variants/LogoutModal';
import { DislikeModal } from 'components/common/Modal/variants/DislikeModal';
import { useModalService } from 'hooks/useModalService';
import { useModalContext } from 'hooks/utils/useModalContext';
import { ModalContextValueProps, ModalVariant } from 'types/Modal';
import { TagModal } from 'components/common/Modal/variants/TagModal';
import { KeywordModal } from 'components/common/Modal/variants/KeywordModal';
import { ImageModal } from 'components/common/Modal/variants/ImageModal';
import { VideoModal } from 'components/common/Modal/variants/VideoModal';
import { FtuVideoModal } from 'components/common/Modal/variants/FtuVideoModal';
import { StopEditingModal } from 'components/common/Modal/variants/StopEditingModal';
import { SubscribeModal } from 'components/common/Modal/variants/SubscribeModal/SubscribeModal';
import { PaywallModal } from 'components/common/Modal/variants/PaywallModal';
import { useLocation } from 'react-router-dom';
import { usePrevious } from '@chakra-ui/react';
import { VoucherModal } from 'components/common/Modal/variants/VoucherModal';
import { ShareModal } from 'components/common/Modal/variants/ShareModal';
import { MailVerificationModal } from 'components/common/Modal/variants/MailVerificationModal';
import { ConfirmationModal } from 'components/common/Modal/variants/ConfirmationModal';
import { AnnouncementModal } from 'components/common/Modal/variants/AnnouncementModal';
import { AdminInviteModal } from 'components/common/Modal/variants/AdminInviteModal';
import { UnsavedChangesModal } from 'components/common/Modal/variants/UnsavedChangesModal';
import { WorksheetVideoModal } from 'components/common/Modal/variants/worksheet/WorksheetVideoModal';
import { WorksheetArticleModal } from 'components/common/Modal/variants/worksheet/WorksheetArticleModal';
import { WorksheetDistributionModal } from 'components/common/Modal/variants/worksheet/WorksheetDistributionModal';
import { BetaWallModal } from 'components/common/Modal/variants/BetaWallModal';
import { WorksheetDeleteBlockModal } from 'components/common/Modal/variants/worksheet/WorksheetDeleteBlockModal';
import { WorksheetDistributionSaveModal } from 'components/common/Modal/variants/worksheet/WorksheetDistributionSaveModal';
import { WorksheetDeleteModal } from 'components/common/Modal/variants/worksheet/WorksheetDeleteModal';
import { WorksheetShareModal } from 'components/common/Modal/variants/worksheet/WorksheetShareModal';
import { WorksheetTemplateCopyModal } from 'components/common/Modal/variants/worksheet/WorksheetTemplateCopyModal';
import { AiProductsModal } from 'components/common/Modal/variants/AiProductsModal/AiProductsModal';
import { AiSubCanceledModal } from 'components/common/Modal/variants/AiSubCanceledModal';
import { AiSubActiveModal } from 'components/common/Modal/variants/AiSubActiveModal';
import { CaseMeModal } from 'components/common/Modal/variants/CaseMeModal';
import { MainSubExpiresModal } from 'components/common/Modal/variants/MainSubExpiresModal';
import { ClassCreationModal } from 'components/common/Modal/variants/ClassCreationModal';
import { ClassParticipantCreationModal } from 'components/common/Modal/variants/ClassParticipantCreationModal';
import { ClassTeacherCreationModal } from 'components/common/Modal/variants/ClassTeacherCreationModal';
import { ClassGuideCreationModal } from 'components/common/Modal/variants/ClassGuideCreationModal';
import { ClassParticipantRemovalModal } from 'components/common/Modal/variants/ClassParticipantRemovalModal';
import { ClassTeacherRemovalModal } from 'components/common/Modal/variants/ClassTeacherRemovalModal';
import { ClassGuideRemovalModal } from 'components/common/Modal/variants/ClassGuideRemovalModal';
import { DeleteClassModal } from 'components/common/Modal/variants/DeleteClassModal';
import { UserClassRoleChangeModal } from 'components/common/Modal/variants/UserClassRoleChangeModal';
import { UserWorksheetsModal } from 'components/common/Modal/variants/worksheet/UserWorksheetsModal';
import { SearchFilterModal } from 'components/common/Modal/variants/SearchFilterModal';
import { WorksheetTemplateDeleteModal } from 'components/common/Modal/variants/WorksheetTemplateDeleteModal';
import { EducationProgressionModal } from 'components/common/Modal/variants/EducationProgressionModal';

type Props = {
  children: ReactElement | ReactElement[];
};

export const ModalProvider = ({ children }: Props): ReactElement => {
  const { currentModalValues } = useModalContext();
  const modalService = useModalService();
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);

  const isModalShown = currentModalValues.isShown;

  const handleClose = (): void => {
    modalService.hide();
  };

  // hide modal when pathname changes (due to paywall)
  useEffect(() => {
    if (pathname !== prevPathname && currentModalValues.isShown) modalService.hide();
  }, [pathname, modalService, currentModalValues, prevPathname]);

  return (
    <>
      <CurrentModal
        currentModalValues={currentModalValues}
        handleClose={handleClose}
        isShown={isModalShown}
        key={
          currentModalValues.variant === ModalVariant.PAYWALL ||
          currentModalValues.variant === ModalVariant.MAIL_VERIFICATION
            ? currentModalValues.target
            : 'default'
        }
      />
      {children}
    </>
  );
};
type CurrentModalProps = {
  currentModalValues: ModalContextValueProps;
  handleClose: () => void;
  isShown: boolean;
};

const CurrentModal = ({
  currentModalValues,
  handleClose,
  isShown,
}: CurrentModalProps): ReactElement | null => {
  switch (currentModalValues.variant) {
    case ModalVariant.LOGOUT:
      return <LogoutModal isShown={isShown} handleClose={handleClose} />;
    case ModalVariant.DISLIKE:
      return (
        <DislikeModal
          isShown={isShown}
          handleClose={handleClose}
          name={currentModalValues.name}
          onAccept={currentModalValues.onAccept}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.TAG:
      return (
        <TagModal
          isShown={isShown}
          handleClose={handleClose}
          title={currentModalValues.title}
          tagDescription={currentModalValues.tagDescription}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.KEYWORD:
      return (
        <KeywordModal
          isShown={isShown}
          handleClose={handleClose}
          data={currentModalValues.data}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.IMAGE:
      return (
        <ImageModal
          isShown={isShown}
          handleClose={handleClose}
          title={currentModalValues.title}
          imgUrl={currentModalValues.imgUrl}
          description={currentModalValues.description}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.VIDEO:
      return (
        <VideoModal
          isShown={isShown}
          handleClose={handleClose}
          videoUrl={currentModalValues.videoUrl}
          videoDescription={currentModalValues.videoDescription}
          thumbnailUrl={currentModalValues.thumbnailUrl}
          isEmbedded={currentModalValues.isEmbedded}
          onVideoEnd={currentModalValues.onVideoEnd}
          onVideoAbort={currentModalValues.onVideoAbort}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.FTU_VIDEO:
      return (
        <FtuVideoModal
          isShown={isShown}
          handleClose={handleClose}
          video={currentModalValues.video}
          onModalClose={currentModalValues.onModalClose}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.STOP_EDITING:
      return (
        <StopEditingModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.SUBSCRIBE:
      return (
        <SubscribeModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.PAYWALL:
      return (
        <PaywallModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          target={currentModalValues.target}
          onUpgradeClick={currentModalValues.onUpgradeClick}
        />
      );
    case ModalVariant.BETA_WALL:
      return (
        <BetaWallModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.MAIL_VERIFICATION:
      return (
        <MailVerificationModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          target={currentModalValues.target}
          shouldGoBack={currentModalValues.shouldGoBack}
        />
      );
    case ModalVariant.VOUCHER:
      return (
        <VoucherModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          onSuccess={currentModalValues.onSuccess}
        />
      );
    case ModalVariant.SHARE:
      return (
        <ShareModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
        />
      );
    case ModalVariant.CONFIRMATION:
      return (
        <ConfirmationModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          onConfirm={currentModalValues.onConfirm}
        />
      );
    case ModalVariant.ANNOUNCEMENT: {
      return (
        <AnnouncementModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          message={currentModalValues.message}
          title={currentModalValues.title}
          onClose={currentModalValues.onClose}
          internalLink={currentModalValues.internalLink}
          externalLink={currentModalValues.externalLink}
        />
      );
    }
    case ModalVariant.ADMIN_INVITE: {
      return (
        <AdminInviteModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          selectedVoucherId={currentModalValues.selectedVoucherId}
          allVouchers={currentModalValues.allVouchers}
        />
      );
    }
    case ModalVariant.WORKSHEET_VIDEO: {
      return (
        <WorksheetVideoModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          onSelect={currentModalValues.onSelect}
        />
      );
    }
    case ModalVariant.WORKSHEET_ARTICLE: {
      return (
        <WorksheetArticleModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          onSelect={currentModalValues.onSelect}
        />
      );
    }
    case ModalVariant.WORKSHEET_DISTRIBUTION: {
      return (
        <WorksheetDistributionModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          worksheetId={currentModalValues.worksheetId}
          worksheetTitle={currentModalValues.worksheetTitle}
        />
      );
    }
    case ModalVariant.WORKSHEET_DISTRIBUTION_SAVE: {
      return (
        <WorksheetDistributionSaveModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          onCopyAndSave={currentModalValues.onCopyAndSave}
          onPrimarySave={currentModalValues.onPrimarySave}
          onCancel={currentModalValues.onCancel}
          mode={currentModalValues.mode}
        />
      );
    }
    case ModalVariant.WORKSHEET_DELETE_BLOCK: {
      return (
        <WorksheetDeleteBlockModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          name={currentModalValues.name}
          onAccept={currentModalValues.onAccept}
          onCancel={currentModalValues.onCancel}
        />
      );
    }
    case ModalVariant.WORKSHEET_DELETE: {
      return (
        <WorksheetDeleteModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          description={currentModalValues.description}
          onDelete={currentModalValues.onDelete}
        />
      );
    }
    case ModalVariant.WORKSHEET_SHARE: {
      return (
        <WorksheetShareModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          worksheetId={currentModalValues.worksheetId}
          key={currentModalValues.worksheetId}
        />
      );
    }
    case ModalVariant.WORKSHEET_TEMPLATE_COPY: {
      return (
        <WorksheetTemplateCopyModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          templateId={currentModalValues.templateId}
          initialTitle={currentModalValues.initialTitle}
          onCopied={currentModalValues.onCopied}
        />
      );
    }
    case ModalVariant.WORKSHEET_TEMPLATE_DELETE: {
      return (
        <WorksheetTemplateDeleteModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          templateName={currentModalValues.templateName}
          onDelete={currentModalValues.onDelete}
        />
      );
    }
    case ModalVariant.UNSAVED_CHANGES: {
      return (
        <UnsavedChangesModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          message={currentModalValues.message}
          onConfirm={currentModalValues.onConfirm}
          onCancel={currentModalValues.onCancel}
          onSaveAndLeave={currentModalValues.onSaveAndLeave}
        />
      );
    }
    case ModalVariant.AI_PRODUCTS: {
      return (
        <AiProductsModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          products={currentModalValues.products}
          pendingChange={currentModalValues.pendingChange}
          isSubExpiring={currentModalValues.isSubExpiring}
        />
      );
    }
    case ModalVariant.AI_SUB_CANCELED: {
      return (
        <AiSubCanceledModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          endDate={currentModalValues.endDate}
        />
      );
    }
    case ModalVariant.AI_SUB_STILL_ACTIVE: {
      return (
        <AiSubActiveModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          endDate={currentModalValues.endDate}
        />
      );
    }
    case ModalVariant.CASE_ME_NEWSLETTER: {
      return (
        <CaseMeModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          onClose={currentModalValues.onClose}
          id={currentModalValues.id}
          onRedirect={currentModalValues.onRedirect}
        />
      );
    }
    case ModalVariant.MAIN_SUB_EXPIRES: {
      return (
        <MainSubExpiresModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          endDate={currentModalValues.endDate}
        />
      );
    }
    case ModalVariant.CLASS_CREATION: {
      return (
        <ClassCreationModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
        />
      );
    }
    case ModalVariant.CLASS_PARTICIPANT_CREATION: {
      return (
        <ClassParticipantCreationModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.CLASS_TEACHER_CREATION: {
      return (
        <ClassTeacherCreationModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.CLASS_GUIDE_CREATION: {
      return (
        <ClassGuideCreationModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.CLASS_PARTICIPANT_REMOVAL: {
      return (
        <ClassParticipantRemovalModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          participantId={currentModalValues.participantId}
          participantName={currentModalValues.participantName}
          participantType={currentModalValues.participantType}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.CLASS_TEACHER_REMOVAL: {
      return (
        <ClassTeacherRemovalModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          teacherId={currentModalValues.teacherId}
          teacherName={currentModalValues.teacherName}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.CLASS_GUIDE_REMOVAL: {
      return (
        <ClassGuideRemovalModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          guideId={currentModalValues.guideId}
          guideName={currentModalValues.guideName}
          classId={currentModalValues.classId}
        />
      );
    }
    case ModalVariant.DELETE_CLASS: {
      return (
        <DeleteClassModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          onConfirm={currentModalValues.onConfirm}
          className={currentModalValues.className}
        />
      );
    }
    case ModalVariant.USER_WORKSHEETS: {
      return (
        <UserWorksheetsModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          classId={currentModalValues.classId}
          studentId={currentModalValues.studentId}
        />
      );
    }

    case ModalVariant.MANAGEMENT_CHANGE_ROLE: {
      return (
        <UserClassRoleChangeModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          onConfirm={currentModalValues.onConfirm}
          firstName={currentModalValues.firstName}
          lastName={currentModalValues.lastName}
          newRole={currentModalValues.newRole}
          currentRole={currentModalValues.currentRole}
          memberOfClasses={currentModalValues.memberOfClasses}
          teacherOfClasses={currentModalValues.teacherOfClasses}
          guideOfClasses={currentModalValues.guideOfClasses}
        />
      );
    }
    case ModalVariant.SEARCH_FILTER: {
      return (
        <SearchFilterModal
          isShown={isShown}
          handleClose={handleClose}
          variant={currentModalValues.variant}
          selectedCeTags={currentModalValues.selectedCeTags}
          selectedKbTags={currentModalValues.selectedKbTags}
          setSelectedCeTags={currentModalValues.setSelectedCeTags}
          setSelectedKbTags={currentModalValues.setSelectedKbTags}
        />
      );
    }
    case ModalVariant.EDUCATION_PROGRESSION: {
      return (
        <EducationProgressionModal
          variant={currentModalValues.variant}
          isShown={isShown}
          handleClose={handleClose}
          educationProgressionPrompt={currentModalValues.educationProgressionPrompt}
          onConfirm={currentModalValues.onConfirm}
          onAdjust={currentModalValues.onAdjust}
          onDismiss={currentModalValues.onDismiss}
          isSubmitting={currentModalValues.isSubmitting}
        />
      );
    }
    default:
      return null;
  }
};
